import { API_VERSION } from '../api/index';

/**
 * Creates Parent API method implementations
 * @param {Object} dependencies - React context values and utilities
 * @param {Object} dependencies.toastManager - Toast manager instance
 * @param {Function} dependencies.navigate - React Router navigate function
 * @param {Object} dependencies.user - Current user from auth context
 * @returns {Object} Parent API methods
 */
export function createParentApiMethods({ toastManager, navigate, user }) {
  return {
    /**
     * Show a notification to the user
     * @param {string} message - Notification message
     * @param {string} type - Notification type: 'success', 'error', 'info', 'warning'
     * @returns {Promise<{success: boolean}>}
     */
    showNotification: async (message, type = 'info') => {
      if (!message || typeof message !== 'string') {
        throw new Error('showNotification requires a message string');
      }

      const validTypes = ['success', 'error', 'info', 'warning'];
      if (!validTypes.includes(type)) {
        throw new Error(`Invalid notification type: ${type}. Must be one of: ${validTypes.join(', ')}`);
      }

      try {
        toastManager.show(message, type);
        return { success: true };
      } catch (error) {
        console.error('[ParentAPI] showNotification failed:', error);
        throw new Error(`Failed to show notification: ${error.message}`);
      }
    },

    /**
     * Navigate to a Silo route
     * @param {string} route - Route path (e.g., '/spaces/apps', '/plugins/123')
     * @returns {Promise<{success: boolean}>}
     */
    navigate: async (route) => {
      if (!route || typeof route !== 'string') {
        throw new Error('navigate requires a route string');
      }

      try {
        navigate(route);
        return { success: true };
      } catch (error) {
        console.error('[ParentAPI] navigate failed:', error);
        throw new Error(`Failed to navigate to ${route}: ${error.message}`);
      }
    },

    /**
     * Get current user context
     * @returns {Promise<Object>} User object with id, email, name, admin status, etc.
     */
    getUserContext: async () => {
      try {
        if (!user) {
          return null;
        }

        // Return a safe copy of user data (don't expose sensitive info)
        return {
          id: user.id,
          email: user.email,
          name: user.name,
          picture: user.picture,
          admin: user.admin || false,
          isAuthenticated: true
        };
      } catch (error) {
        console.error('[ParentAPI] getUserContext failed:', error);
        throw new Error(`Failed to get user context: ${error.message}`);
      }
    },

    /**
     * Make an authenticated API call through Silo
     * @param {string} endpoint - API endpoint path (e.g., '/plugins')
     * @param {Object} options - Fetch options (method, body, headers, etc.)
     * @returns {Promise<any>} Response data (parsed JSON)
     */
    requestApi: async (endpoint, options = {}) => {
      if (!endpoint || typeof endpoint !== 'string') {
        throw new Error('requestApi requires an endpoint string');
      }

      try {
        // Build full URL
        const baseUrl = ''; // Relative URLs (from utils/api/index.js pattern)
        const url = endpoint.startsWith('/') 
          ? `${baseUrl}${API_VERSION}${endpoint}`
          : `${baseUrl}${API_VERSION}/${endpoint}`;

        // Prepare fetch options
        const fetchOptions = {
          method: options.method || 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            ...(options.headers || {})
          },
          credentials: 'include', // Important: include cookies for authentication
          ...options
        };

        // Add body if present (and not already stringified)
        if (options.body) {
          fetchOptions.body = typeof options.body === 'string' 
            ? options.body 
            : JSON.stringify(options.body);
        }

        // Make request
        const response = await fetch(url, fetchOptions);

        // Handle response
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
          const errorMessage = errorData.detail || `API request failed with status ${response.status}`;
          throw new Error(errorMessage);
        }

        // Parse and return JSON
        const data = await response.json();
        return data;
      } catch (error) {
        console.error('[ParentAPI] requestApi failed:', error);
        throw new Error(`API request failed: ${error.message}`);
      }
    }
  };
}

